import React from 'react';
import { Card, Table, Typography, Button, Drawer, Image, Empty, notification, Space, Modal, Input } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import { APP_DATE_FORMAT } from 'config/constants';
import { confirmMeasuringInstrumentInspector, rejectMeasuringInstrumentInspector, listMeasuringInstrumentInspectorsMe } from '../../measuring-instrument-inspectors/services/measuring-instrument-inspectorService';

interface DeviceAssignmentCardProps {
  showActions?: boolean;
  title?: React.ReactNode;
  query?: string;
}

const { Text } = Typography;
const { TextArea } = Input;

const DARK_GREEN_BUTTON_STYLE: React.CSSProperties = {
  backgroundColor: '#1f6f43',
  borderColor: '#1f6f43',
  color: '#fff',
};

const getStatusLabel = (record: any) => {
  if (record?.confirmedAt) {
    return 'Onaylandı';
  }
  if (record?.rejectedAt) {
    return 'Reddedildi';
  }
  return 'Beklemede';
};

export default function DeviceAssignmentCard({
  showActions = true,
  title,
  query,
}: DeviceAssignmentCardProps) {
  const [data, setData] = React.useState<any[]>([]);
  const [api, contextHolder] = notification.useNotification();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<any>();
  const [confirming, setConfirming] = React.useState(false);
  const [rejectingId, setRejectingId] = React.useState<number | null>(null);
  const [rejectModalOpen, setRejectModalOpen] = React.useState(false);
  const [rejectReason, setRejectReason] = React.useState('');
  const [rejectRecord, setRejectRecord] = React.useState<any>();
  const [assignmentSortOrder, setAssignmentSortOrder] = React.useState<'ascend' | 'descend'>('ascend');

  const getSortParam = React.useCallback(
    (order: 'ascend' | 'descend') => (order === 'descend' ? 'assignmentAt,desc' : 'assignmentAt,asc'),
    [],
  );

  const fetchData = React.useCallback(async () => {
    try {
      const params = new URLSearchParams(query ?? 'confirmedAt.specified=false&rejectedAt.specified=false');
      params.set('sort', getSortParam(assignmentSortOrder));
      const res = await listMeasuringInstrumentInspectorsMe(params.toString());
      const items = res;
      const pending = items.filter((r: any) => !r?.confirmedAt && !r?.rejectedAt);
      setData(pending);
    } catch {
      setData([]);
    }
  }, [assignmentSortOrder, getSortParam, query]);
  React.useEffect(() => { fetchData(); }, [fetchData]);

  const handleTableChange: TableProps<any>['onChange'] = (_pagination, _filters, sorter) => {
    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextOrder = nextSorter?.order === 'descend' ? 'descend' : 'ascend';
    if (nextOrder !== assignmentSortOrder) {
      setAssignmentSortOrder(nextOrder);
    }
  };

  const handleView = (record: any) => {
    setSelected(record);
    setDrawerOpen(true);
  };

  const handleClose = () => {
    if (confirming) return;
    setDrawerOpen(false);
    setSelected(undefined);
  };

  const handleConfirm = async () => {
    if (!selected) return;
    setConfirming(true);
    try {
      await confirmMeasuringInstrumentInspector(selected.id);
      api.success({ message: 'Onaylandı' });
      setDrawerOpen(false);
      setSelected(undefined);
      await fetchData();
    } catch {
      api.error({ message: 'Onaylanamadı' });
    } finally {
      setConfirming(false);
    }
  };

  const openRejectModal = (record: any) => {
    setRejectRecord(record);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const closeRejectModal = () => {
    if (rejectingId !== null) return;
    setRejectModalOpen(false);
    setRejectReason('');
    setRejectRecord(undefined);
  };

  const handleReject = async () => {
    if (!rejectRecord?.id) return;
    const reason = rejectReason.trim();
    if (!reason) {
      api.warning({ message: 'Reddetme nedeni zorunludur' });
      return;
    }

    setRejectingId(rejectRecord.id);
    try {
      await rejectMeasuringInstrumentInspector(rejectRecord.id, reason);
      api.success({ message: 'Cihaz ataması reddedildi' });
      if (selected?.id === rejectRecord.id) {
        setDrawerOpen(false);
        setSelected(undefined);
      }
      setRejectModalOpen(false);
      setRejectReason('');
      setRejectRecord(undefined);
      await fetchData();
    } catch {
      api.error({ message: 'Cihaz ataması reddedilemedi' });
    } finally {
      setRejectingId(null);
    }
  };

  const columns: any[] = [
    {
      title: 'Ölçüm Cihazı',
      dataIndex: 'measuringInstrumentInventory.measuringInstrument.name',
      render: (_: any, record: any) => record?.measuringInstrumentInventory?.measuringInstrument?.name || '',
    },
    {
      title: 'Marka',
      dataIndex: 'measuringInstrumentInventory.brand',
      render: (_: any, record: any) => record?.measuringInstrumentInventory?.brand || '',
    },
    {
      title: 'Model',
      dataIndex: 'measuringInstrumentInventory.model',
      render: (_: any, record: any) => record?.measuringInstrumentInventory?.model || '',
    },
    {
      title: 'Seri Numarası',
      dataIndex: 'measuringInstrumentInventory.serialNumber',
      render: (_: any, record: any) => record?.measuringInstrumentInventory?.serialNumber || '',
    },
    {
      title: 'Teslim Tarihi',
      dataIndex: 'assignmentAt',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: assignmentSortOrder,
      render: (value: string) => (value ? dayjs(value).format(APP_DATE_FORMAT) : ''),
    },
  ];

  if (showActions) {
    columns.push({
      title: 'İşlemler',
      render: (_: any, record: any) => (
        record?.confirmedAt || record?.rejectedAt ? null : (
          <Space>
            <Button type='primary' style={DARK_GREEN_BUTTON_STYLE} onClick={() => handleView(record)}>Görüntüle</Button>
            <Button danger loading={rejectingId === record.id} onClick={() => openRejectModal(record)}>Cihazı Reddet</Button>
          </Space>
        )
      ),
    });
  } else {
    columns.push({
      title: 'Durum',
      render: (_: any, record: any) => getStatusLabel(record),
    });
  }

  const photoUrls: string[] = Array.isArray(selected?.photoUrls)
    ? selected.photoUrls.filter((url: any) => typeof url === 'string' && url)
    : [];

  const photoGrid = photoUrls.length ? (
    <Image.PreviewGroup>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 12 }}>
        {photoUrls.map((url, idx) => (
          <Image
            key={`${idx}-${url}`}
            src={url}
            alt={`cihaz-foto-${idx + 1}`}
            width='100%'
            style={{ objectFit: 'cover', borderRadius: 8 }}
          />
        ))}
      </div>
    </Image.PreviewGroup>
  ) : (
    <Empty description='Fotoğraf bulunamadı' />
  );

  return (
    <Card
      className='br-30px'
      title={
        title ?? (
          <Typography.Title level={4} style={{ margin: 0 }}>
            Cihaz Atama Listesi <Text type='secondary'><strong>({data.length})</strong> kayıt listelendi</Text>
          </Typography.Title>
        )
      }
    >
      {contextHolder}
      <Table
        rowKey={(record: any) => record.id}
        dataSource={data}
        columns={columns as any}
        pagination={false}
        onChange={handleTableChange}
      />
      {showActions && (
        <Drawer
          open={drawerOpen}
          onClose={handleClose}
          width={520}
          destroyOnClose
          title='Cihaz Fotoğrafları'
          extra={
            <Button type='primary' style={DARK_GREEN_BUTTON_STYLE} onClick={handleConfirm} loading={confirming} disabled={!selected}>
              Cihazı Kabul Et
            </Button>
          }
        >
          <Text type='secondary'>Cihazın teslim anındaki fotoğrafları aşağıda bulunmaktadır.</Text>
          <div style={{ height: 12 }} />
          {photoGrid}
        </Drawer>
      )}
      <Modal
        open={rejectModalOpen}
        title='Cihazı Reddet'
        onCancel={closeRejectModal}
        onOk={handleReject}
        okText='Kaydet'
        cancelText='İptal'
        okButtonProps={{ loading: rejectingId === rejectRecord?.id }}
        destroyOnClose
      >
        <Text strong>Reddetme Nedeni</Text>
        <div style={{ height: 8 }} />
        <TextArea
          rows={4}
          value={rejectReason}
          onChange={(event) => setRejectReason(event.target.value)}
          placeholder='Reddetme nedenini yazınız'
        />
      </Modal>
    </Card>
  );
}
